import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
  Alert,
  Box,
  Button,
  ClickAwayListener,
  InputLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { useToast } from "@qubit/autoparts";
import { useState } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";

import { useTranslation } from "react-i18next";
import ReactJson from "react-json-view";

import { useAppSelector } from "~/app/store";
import { useClientConfig } from "~/hooks/useClientConfig";
import { useSummarizedVariantInventorySummaries } from "~/hooks/useSummarizedVariantInventorySummaries";

import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { useCreatePepsiOrderMutation } from "~/redux/pepsi/pickOrder.hooks";
import { usePostOrdersCreateMutation } from "~/redux/public/orders.openApi";
import { selectUsersClientId } from "~/redux/selectors/authSelectors";
import { selectOrderCreationSelectedAutostoreGridId } from "~/redux/selectors/orderCreationSelectors";

import { selectUsersFulfillmentCenter } from "~/redux/selectors/storeSelectors";
import { maxBy, minBy } from "~/redux/warehouse/autostoreGrid.transforms";
import {
  usePostToolsOrdersGenerateMutation,
  GenerateOrders,
  DistributionProfile
} from "~/redux/warehouse/tools.openApi";

import { DistributionProfileField } from "./DistributionProfileField";
import { DistributionProfileFields } from "./DistributionProfileFields";
import { translateToPickOrder } from "./translateToPepsiApi";

const avgCount = (profile?: DistributionProfile[]) => {
  const totalWeight =
    profile?.reduce((sum, item) => sum + item.selectionWeight, 0) ?? 0;

  return (
    profile?.reduce(
      (sum, item) => sum + (item.count * item.selectionWeight) / totalWeight,
      0
    ) ?? 0
  );
};

export const GenerateOrdersPage = () => {
  const { t } = useTranslation();
  const { errorToast, successToast } = useToast();
  const autostoreGridId = useAppSelector(
    selectOrderCreationSelectedAutostoreGridId
  );
  const clientId = useAppSelector(selectUsersClientId);
  const fulfillmentCenter = useAppSelector(selectUsersFulfillmentCenter);
  const [generateOrders, { data: orderData, isLoading }] =
    usePostToolsOrdersGenerateMutation();

  const { dev_useV2OrderCreation } = useClientConfig();
  const [createPickOrder, { isLoading: createPickOrderInProgress }] =
    useCreatePepsiOrderMutation();
  const [createOrder, { isLoading: createOrderInProgress }] =
    usePostOrdersCreateMutation();
  const [showOrderData, setShowOrderData] = useState(false);
  const [dryRun, setDryRun] = useState(false);

  const { totalAvailableInventory } =
    useSummarizedVariantInventorySummaries(autostoreGridId);

  const generateOrdersContext = useForm<GenerateOrders>({
    defaultValues: {
      numberOfOrders: 10,
      pickingEndTimeMinutes: 60,
      toteDistributionProfile: [
        { count: 2, selectionWeight: 7.5 },
        { count: 5, selectionWeight: 2.5 }
      ],
      lineItemDistributionProfile: [
        { count: 2, selectionWeight: 5 },
        { count: 3, selectionWeight: 2 },
        { count: 7, selectionWeight: 0.1 }
      ],
      unitCountDistributionProfile: [
        { count: 1, selectionWeight: 1 },
        { count: 3, selectionWeight: 1 },
        { count: 5, selectionWeight: 1 }
      ]
    }
  });

  const handleGenerateOrders = async (orders: GenerateOrders) => {
    if (!clientId || !fulfillmentCenter || !autostoreGridId || !orders) return;

    try {
      const data = await generateOrders({
        generateOrders: {
          ...orders,
          gridIds: [autostoreGridId]
        }
      }).unwrap();

      if (dryRun) return;
      if (dev_useV2OrderCreation) {
        await Promise.all(
          data.map((o) =>
            createPickOrder({
              clientId,
              fulfillmentCenterId: fulfillmentCenter.fulfillmentCenterId,
              ...translateToPickOrder(o, fulfillmentCenter)
            })
          )
        );
      } else {
        await Promise.all(data.map((o) => createOrder({ createOrder: o })));
      }
      successToast(`Created ${data.length} order(s)`);
    } catch (err) {
      errorToast(getMessageFromRtkError(err));
    }
  };

  const isGeneratedTotes =
    fulfillmentCenter?.toteConfiguration === "GeneratedTotes";
  const data = generateOrdersContext.watch();

  const calculateQuantityConsumed = (profile?: DistributionProfile[]) => {
    if (!profile) return { min: 0, max: 0 };
    const max = maxBy(profile, (item) => item.count);
    const min = minBy(profile, (item) => item.count);
    return { min, max };
  };

  const totes = calculateQuantityConsumed(data.toteDistributionProfile);
  const lineItems = calculateQuantityConsumed(data.lineItemDistributionProfile);
  const units = calculateQuantityConsumed(data.unitCountDistributionProfile);

  const quantityConsumedMin =
    data.numberOfOrders *
    (isGeneratedTotes ? 1 : totes.min) *
    lineItems.min *
    units.min;

  const quantityConsumedMax =
    data.numberOfOrders *
    (isGeneratedTotes ? 1 : totes.max) *
    lineItems.max *
    units.max;

  const numberOfOrdersErrorMessage =
    generateOrdersContext.formState.errors.numberOfOrders?.message;

  const pickingEndTimeMinutesErrorMessage =
    generateOrdersContext.formState.errors.pickingEndTimeMinutes?.message;

  const {
    fields: toteDistributionProfileFields,
    append: appendToteDistributionProfile,
    remove: removeToteDistributionProfile
  } = useFieldArray({
    control: generateOrdersContext.control,
    name: "toteDistributionProfile"
  });

  const {
    fields: lineItemDistributionProfileFields,
    append: appendLineItemDistributionProfile,
    remove: removeLineItemDistributionProfile
  } = useFieldArray({
    control: generateOrdersContext.control,
    name: "lineItemDistributionProfile"
  });

  const {
    fields: unitCountDistributionProfileFields,
    append: appendUnitCountDistributionProfile,
    remove: removeUnitCountDistributionProfile
  } = useFieldArray({
    control: generateOrdersContext.control,
    name: "unitCountDistributionProfile"
  });

  const [showDistributionProfileInfo, setShowDistributionProfileInfo] =
    useState(false);

  return (
    <>
      <FormProvider {...generateOrdersContext}>
        <form
          onSubmit={generateOrdersContext.handleSubmit(handleGenerateOrders)}
        >
          <Stack gap={2}>
            <Typography variant="h5">Generate Orders</Typography>
            <TextField
              {...generateOrdersContext.register(`numberOfOrders`, {
                required: {
                  value: true,
                  message: "Number of Orders is required"
                },
                min: {
                  value: 1,
                  message: "Number of Orders must be greater than zero"
                }
              })}
              sx={{ width: "150px" }}
              variant="standard"
              label="Number of Orders"
              type="number"
              error={!!numberOfOrdersErrorMessage}
            />
            {!!numberOfOrdersErrorMessage && (
              <Alert severity="error">
                <Typography>{numberOfOrdersErrorMessage}</Typography>
              </Alert>
            )}
            <TextField
              {...generateOrdersContext.register("pickingEndTimeMinutes", {
                min: {
                  value: 1,
                  message: "Picking End Time Minutes must be greater than zero"
                }
              })}
              sx={{ width: "150px" }}
              variant="standard"
              label="Picking End Time Minutes"
              type="number"
              error={!!pickingEndTimeMinutesErrorMessage}
            />
            {!!numberOfOrdersErrorMessage && (
              <Alert severity="error">
                <Typography>{numberOfOrdersErrorMessage}</Typography>
              </Alert>
            )}
            <Typography variant="h6">
              Distribution Profiles{" "}
              <ClickAwayListener
                onClickAway={() => setShowDistributionProfileInfo(false)}
              >
                <Tooltip
                  role="button"
                  aria-hidden="false"
                  aria-label="distribution profile tooltip"
                  title={
                    <Typography variant="body2">
                      {t("distribution profile tooltip")}
                    </Typography>
                  }
                  open={showDistributionProfileInfo}
                  disableHoverListener
                  placement="right"
                >
                  <HelpOutlineIcon
                    sx={{
                      cursor: "pointer",
                      color: "gray"
                    }}
                    onClick={() => setShowDistributionProfileInfo(true)}
                  />
                </Tooltip>
              </ClickAwayListener>
            </Typography>
            <Stack gap={2} flexDirection="row" flexWrap="wrap">
              {!isGeneratedTotes && (
                <DistributionProfileFields
                  avg={avgCount(data.toteDistributionProfile)}
                  onAdd={() =>
                    appendToteDistributionProfile({
                      count: 1,
                      selectionWeight: 1
                    })
                  }
                  label="Totes"
                >
                  {toteDistributionProfileFields.map((field, index) => (
                    <DistributionProfileField<GenerateOrders>
                      key={field.id}
                      propertyName="toteDistributionProfile"
                      index={index}
                      removeItem={removeToteDistributionProfile}
                    />
                  ))}
                </DistributionProfileFields>
              )}
              <DistributionProfileFields
                avg={avgCount(data.lineItemDistributionProfile)}
                onAdd={() =>
                  appendLineItemDistributionProfile({
                    count: 1,
                    selectionWeight: 1
                  })
                }
                label="Line Items"
              >
                {lineItemDistributionProfileFields.map((field, index) => (
                  <DistributionProfileField<GenerateOrders>
                    key={field.id}
                    propertyName="lineItemDistributionProfile"
                    index={index}
                    removeItem={removeLineItemDistributionProfile}
                  />
                ))}
              </DistributionProfileFields>
              <DistributionProfileFields
                avg={avgCount(data.unitCountDistributionProfile)}
                onAdd={() =>
                  appendUnitCountDistributionProfile({
                    count: 1,
                    selectionWeight: 1
                  })
                }
                label="Unit Count"
              >
                {unitCountDistributionProfileFields.map((field, index) => (
                  <DistributionProfileField<GenerateOrders>
                    key={field.id}
                    propertyName="unitCountDistributionProfile"
                    index={index}
                    removeItem={removeUnitCountDistributionProfile}
                  />
                ))}
              </DistributionProfileFields>
            </Stack>
            <Typography variant="h5">
              Quantity Consumed: between {Math.floor(quantityConsumedMin)} and{" "}
              {Math.floor(quantityConsumedMax)}
            </Typography>
            <Stack gap={2} flexDirection="row" alignItems="center">
              <Box>
                <InputLabel htmlFor="dry-run">Dry Run</InputLabel>
                <Switch
                  sx={{ margin: 0 }}
                  checked={dryRun}
                  onChange={(e) => setDryRun(e.target.checked || false)}
                />
              </Box>
              <Button
                variant="contained"
                type="submit"
                disabled={
                  isLoading ||
                  createPickOrderInProgress ||
                  createOrderInProgress ||
                  !autostoreGridId ||
                  quantityConsumedMax > totalAvailableInventory
                }
                style={{ alignSelf: "flex-end" }}
              >
                <Typography>Generate</Typography>
              </Button>
              {!autostoreGridId && (
                <Alert severity="warning">
                  <Typography>No Grid Selected</Typography>
                </Alert>
              )}
              {quantityConsumedMax > totalAvailableInventory && (
                <Alert severity="error">
                  <Typography>Not enough available inventory</Typography>
                </Alert>
              )}
            </Stack>
            {orderData && (
              <Box>
                <InputLabel htmlFor="show-data">Show OrderData</InputLabel>
                <Switch
                  checked={showOrderData}
                  onChange={(e) => setShowOrderData(e.target.checked || false)}
                />
              </Box>
            )}
            {showOrderData && orderData && <ReactJson src={orderData} />}
          </Stack>
        </form>
      </FormProvider>
    </>
  );
};
